import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/authContext.ts";
import {
  apiConfigurada,
  crearEfirma,
  formatoOficial,
  formatoVigente,
  listarAltasPadron,
  listarEfirmas,
  obtenerSujetoObligado,
  registrarAltaPadron,
  type AltaPadronRow,
  type EfirmaRow,
  type SujetoObligadoRow,
} from "../../lib/api.ts";
import { C, st, fecha } from "../../ui/theme.ts";
import { Chip, Fila } from "../../ui/componentes.tsx";

/**
 * CONFIGURACIÓN — estado regulatorio del sujeto obligado: datos del SO,
 * inventario de e.firma (SOLO metadatos: Argus nunca custodia la clave privada;
 * sirve para avisar antes del vencimiento, 0017/0019) y formatos de aviso por
 * fracción (formato SAT oficial vs adaptador Argus implementado).
 */

const OPACIDAD_DESHABILITADO = 0.6;
const DIAS_AVISO_EFIRMA = 30;
const HORAS_DIA = 24;
const MIN_HORA = 60;
const SEG_MIN = 60;
const MS_SEG = 1000;
const MS_POR_DIA = HORAS_DIA * MIN_HORA * SEG_MIN * MS_SEG;

interface FormatoEstado {
  fraccion: string;
  oficial: string | null;
  implementado: string | null;
}

function chipEfirma(e: EfirmaRow) {
  const hoy = new Date();
  const hasta = new Date(e.vigente_hasta);
  const dias = Math.floor((hasta.getTime() - hoy.getTime()) / MS_POR_DIA);
  if (e.estado === "revocada") {
    return <Chip tono="gris">Revocada</Chip>;
  }
  if (dias < 0) {
    return <Chip tono="rojo">VENCIDA — no podrás subir informes</Chip>;
  }
  if (dias <= DIAS_AVISO_EFIRMA) {
    return <Chip tono="ambar">Por vencer — {String(dias)} días</Chip>;
  }
  return <Chip tono="verde">Vigente — {String(dias)} días restantes</Chip>;
}

export function ConfiguracionView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [so, setSo] = useState<SujetoObligadoRow | null>(null);
  const [efirmas, setEfirmas] = useState<EfirmaRow[]>([]);
  const [formatos, setFormatos] = useState<FormatoEstado[]>([]);
  const [altasPadron, setAltasPadron] = useState<AltaPadronRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [altaAbierta, setAltaAbierta] = useState(false);
  const [padronFraccionEditando, setPadronFraccionEditando] = useState<string | null>(null);
  const [guardandoPadron, setGuardandoPadron] = useState(false);

  const campo = (k: string) => f[k] ?? "";
  const setCampo = (k: string, v: string) => { setF((prev) => ({ ...prev, [k]: v })); };

  const recargar = useCallback(async () => {
    if (demo) return;
    const [s, e, altas] = await Promise.all([
      obtenerSujetoObligado(token),
      listarEfirmas(token),
      listarAltasPadron(token),
    ]);
    setSo(s);
    setEfirmas(e);
    setAltasPadron(altas);
    const fracciones = ["ART17_V_BIS", "ART17_V_desarrollo", "ART17_XV"];
    const fx = await Promise.all(fracciones.map(async (fr) => {
      try {
        const [oficial, implementado] = await Promise.all([
          formatoOficial(token, fr),
          formatoVigente(token, fr),
        ]);
        return { fraccion: fr, oficial, implementado };
      } catch {
        return { fraccion: fr, oficial: null, implementado: null };
      }
    }));
    setFormatos(fx);
  }, [demo, token]);

  useEffect(() => {
    if (demo) return;
    recargar().catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudo cargar la configuración."); });
  }, [demo, recargar]);

  const listoAlta = campo("numero_serie").trim() !== "" && campo("rfc").trim() !== "" && campo("desde") !== "" && campo("hasta") !== "";
  const formatoTexto = (fraccion: string): string => {
    if (demo) {
      return fraccion === "ART17_V_BIS"
        ? "ANEXO_5B (adaptador Argus activo)"
        : fraccion === "ART17_XV"
          ? "ARRENDAMIENTO_XML (adaptador Argus activo)"
          : "INMUEBLES_XML (adaptador Argus activo)";
    }
    const f = formatos.find((x) => x.fraccion === fraccion);
    if (!f) return "…";
    if (f.implementado) return `${f.implementado} (adaptador Argus activo)`;
    if (f.oficial) return `${f.oficial} (oficial SAT; adaptador Argus pendiente)`;
    return "Sin formato oficial SAT";
  };

  const altaDeFraccion = (fraccion: string): AltaPadronRow | undefined =>
    altasPadron.find((a) => a.fraccion === fraccion);

  const guardarAltaPadron = (fraccion: string) => {
    const fecha = campo(`padron_fecha_${fraccion}`);
    if (!fecha) {
      setError("Captura la fecha de alta antes de guardar.");
      return;
    }
    setGuardandoPadron(true);
    setError(null);
    const run = async () => {
      if (demo) {
        setError("En modo demostración el alta de padrón requiere backend conectado.");
        return;
      }
      await registrarAltaPadron(token, fraccion, fecha, true);
      await recargar();
      setPadronFraccionEditando(null);
    };
    void run()
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudo registrar el alta de padrón."); })
      .finally(() => { setGuardandoPadron(false); });
  };

  const chipAltaPadron = (fraccion: string) => {
    const alta = altaDeFraccion(fraccion);
    if (!alta || !alta.confirmada) {
      return <Chip tono="rojo">Sin alta — bloquea presentar</Chip>;
    }
    return <Chip tono="verde">Confirmada — {fecha(alta.fecha_alta)}</Chip>;
  };

  return (
    <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <h2 style={{ ...st.display, margin: "0 0 2px", fontSize: 20 }}>Configuración</h2>
        <p style={{ margin: "0 0 16px", color: C.muted, fontSize: 13 }}>
          Estado regulatorio del sujeto obligado.
        </p>

        {demo && (
          <div style={{ ...st.tarjeta, background: C.amberBg, borderColor: "#F0D9B5" }}>
            <span style={{ fontSize: 12.5, color: "#6d4a00" }}>
              Modo demostración — conecta el backend para ver el estado regulatorio real.
            </span>
          </div>
        )}
        {error && (
          <div style={{ ...st.tarjeta, background: C.redBg, borderColor: "#F2C4BE" }}>
            <span style={{ fontSize: 13, color: C.red }}>{error}</span>
          </div>
        )}

        <div style={st.tarjeta}>
          <p style={st.eyebrow}>Sujeto obligado</p>
          <Fila k="Razón social" v={so?.razon_social ?? "—"} />
          <Fila k="RFC" v={so?.rfc ?? "—"} />
          <Fila k="Alta de la obligación (padrón)" v={so?.obligacion_desde ? fecha(so.obligacion_desde) : "Sin registrar"} />
          <Fila k="Baja de la obligación" v={so?.obligacion_hasta ? fecha(so.obligacion_hasta) : "Sigue obligado"} />
          <p style={{ margin: "8px 0 0", fontSize: 12, color: C.muted }}>
            La ventana de obligación acota el barrido mensual de informes en ceros (0018).
          </p>
        </div>

        <div style={st.tarjeta}>
          <p style={st.eyebrow}>e.firma de la empresa (inventario — Argus NUNCA guarda tu clave privada)</p>
          {efirmas.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, margin: "0 0 8px" }}>
              Sin e.firma registrada. Registra sus METADATOS (número de serie y vigencia) para
              que Argus te avise 30 días antes de que caduque — renovarla tarda días y sin ella
              no puedes subir informes al SAT.
            </p>
          )}
          {efirmas.map((e) => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: `1px solid ${C.line}` }}>
              <span style={{ fontSize: 13.5, color: C.ink }}>
                <b>{e.numero_serie}</b>
                <span style={{ color: C.muted }}> · {e.rfc} · {fecha(e.vigente_desde)} → {fecha(e.vigente_hasta)}</span>
              </span>
              {chipEfirma(e)}
            </div>
          ))}
          {!altaAbierta ? (
            <button
              style={{ padding: "8px 13px", borderRadius: 10, border: `1px solid ${C.tiel}`, background: "#fff", color: C.tielDark, fontSize: 13, fontWeight: 600, cursor: "pointer", marginTop: 10 }}
              onClick={() => { setAltaAbierta(true); }}
            >
              ＋ Registrar e.firma (metadatos)
            </button>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
              <div>
                <label style={st.label}>Número de serie (20 dígitos)</label>
                <input style={st.input} inputMode="numeric" value={campo("numero_serie")} onChange={(e) => { setCampo("numero_serie", e.target.value.replace(/[^0-9]/g, "")); }} />
              </div>
              <div>
                <label style={st.label}>RFC del certificado</label>
                <input style={st.input} value={campo("rfc")} onChange={(e) => { setCampo("rfc", e.target.value.toUpperCase()); }} />
              </div>
              <div>
                <label style={st.label}>Vigente desde</label>
                <input style={st.input} type="date" value={campo("desde")} onChange={(e) => { setCampo("desde", e.target.value); }} />
              </div>
              <div>
                <label style={st.label}>Vigente hasta</label>
                <input style={st.input} type="date" value={campo("hasta")} onChange={(e) => { setCampo("hasta", e.target.value); }} />
              </div>
              <div style={{ gridColumn: "span 2", display: "flex", gap: 8 }}>
                <button
                  style={{ ...st.btnPrim, opacity: guardando || !listoAlta ? OPACIDAD_DESHABILITADO : 1 }}
                  disabled={guardando || !listoAlta}
                  onClick={() => {
                    setGuardando(true);
                    setError(null);
                    const run = async () => {
                      if (demo) {
                        setError("En modo demostración el alta requiere backend conectado.");
                        return;
                      }
                      await crearEfirma(token, tenantId, {
                        numero_serie: campo("numero_serie").trim(),
                        rfc: campo("rfc").trim(),
                        vigente_desde: campo("desde"),
                        vigente_hasta: campo("hasta"),
                        estado: "vigente",
                      });
                      await recargar();
                      setAltaAbierta(false);
                      setF({});
                    };
                    void run()
                      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudo registrar la e.firma."); })
                      .finally(() => { setGuardando(false); });
                  }}
                >
                  {guardando ? "Guardando…" : "Registrar"}
                </button>
                <button style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.line}`, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }} onClick={() => { setAltaAbierta(false); }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}
          <p style={{ margin: "10px 0 0", fontSize: 12, color: C.muted }}>
            Al registrarla, el sistema crea el reloj de vencimiento (aviso 30 días antes). La
            firma real ocurre en el portal SPPLD con TU e.firma; si no consta vigente, al
            presentar sólo se te ADVIERTE (gate suave).
          </p>
        </div>

        <div style={st.tarjeta}>
          <p style={st.eyebrow}>Formatos de aviso por fracción (el formato es un adaptador)</p>
          <Fila k="V Bis — captación de recursos" v={formatoTexto("ART17_V_BIS")} />
          <Fila k="fr. V — venta de unidades" v={formatoTexto("ART17_V_desarrollo")} />
          <Fila k="fr. XV — arrendamiento de inmuebles" v={formatoTexto("ART17_XV")} />
          <p style={{ margin: "8px 0 0", fontSize: 12, color: C.muted }}>
            Con formato oficial y adaptador activo, el aviso fluye al informe. Si el SAT ya
            publicó el formato pero Argus no tiene adaptador, la obligación queda registrada
            con su reloj hasta que se implemente ese generador.
          </p>
        </div>

        <div style={st.tarjeta}>
          <p style={st.eyebrow}>Alta de padrón SPPLD por fracción</p>
          {(["ART17_V_desarrollo", "ART17_V_BIS", "ART17_XV"] as const).map((fr) => {
            const nombre = fr === "ART17_V_desarrollo"
              ? "fr. V — venta de unidades"
              : fr === "ART17_V_BIS"
                ? "V Bis — captación de recursos"
                : "fr. XV — arrendamiento de inmuebles";
            const editando = padronFraccionEditando === fr;
            return (
              <div key={fr} style={{ padding: "8px 0", borderBottom: `1px solid ${C.line}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13.5, color: C.ink }}>{nombre}</span>
                  {chipAltaPadron(fr)}
                </div>
                {!editando ? (
                  <button
                    style={{ padding: "6px 11px", borderRadius: 8, border: `1px solid ${C.tiel}`, background: "#fff", color: C.tielDark, fontSize: 12.5, fontWeight: 600, cursor: "pointer", marginTop: 8 }}
                    onClick={() => { setPadronFraccionEditando(fr); }}
                  >
                    {altaDeFraccion(fr)?.confirmada ? "Actualizar alta" : "Registrar alta"}
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginTop: 8, flexWrap: "wrap" }}>
                    <div>
                      <label style={st.label}>Fecha de alta</label>
                      <input
                        style={st.input}
                        type="date"
                        value={campo(`padron_fecha_${fr}`)}
                        onChange={(e) => { setCampo(`padron_fecha_${fr}`, e.target.value); }}
                      />
                    </div>
                    <button
                      style={{ ...st.btnPrim, opacity: guardandoPadron ? OPACIDAD_DESHABILITADO : 1 }}
                      disabled={guardandoPadron}
                      onClick={() => { guardarAltaPadron(fr); }}
                    >
                      {guardandoPadron ? "Guardando…" : "Confirmar alta"}
                    </button>
                    <button
                      style={{ padding: "9px 14px", borderRadius: 10, border: `1px solid ${C.line}`, background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                      onClick={() => { setPadronFraccionEditando(null); }}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          <p style={{ margin: "8px 0 0", fontSize: 12, color: C.muted }}>
            Sin alta confirmada, Argus te deja generar el XML de la fracción (con advertencia),
            pero BLOQUEA presentarlo hasta que registres el alta SPPLD correspondiente.
          </p>
        </div>
      </div>
    </main>
  );
}
